import type {ActionPerformed, PushNotificationSchema} from "@capacitor/push-notifications";
import {Capacitor} from "@capacitor/core";
import {AppUpdate} from "@capawesome/capacitor-app-update";

/** Must match APNs custom field / FCM `data.action` from your backend. */
export const PUSH_DATA_ACTION_APP_UPDATE = "update";

/** Schema keys we do not treat as custom payload (likely mirrored from `aps`). */
const NOT_CUSTOM_PAYLOAD_KEYS = new Set([
	"data",
	"title",
	"body",
	"subtitle",
	"id",
	"badge",
	"notification",
	"tag",
	"group",
	"groupSummary",
	"click_action",
	"link"
]);

function normalizeActionValue(raw: unknown): string | undefined {
	if (raw === undefined || raw === null) {
		return undefined;
	}
	if (typeof raw === "string") {
		const t = raw.trim();
		return t.length === 0 ? undefined : t.toLowerCase();
	}
	if (typeof raw === "number" || typeof raw === "boolean") {
		return String(raw).trim().toLowerCase();
	}
	return undefined;
}

function getPayloadAction(data: unknown): string | undefined {
	if (data == null || typeof data !== "object") {
		return undefined;
	}
	const o = data as Record<string, unknown>;
	return (
		normalizeActionValue(o.action) ??
		normalizeActionValue(o.Action)
	);
}

/**
 * Merges `notification.data` with root-level custom keys Capacitor/iOS sometimes exposes
 * next to `title`/`body` (so `action` at APNs root is still visible to JS).
 */
export function mergePushNotificationCustomPayload(
	notification: PushNotificationSchema | undefined
): Record<string, unknown> | undefined {
	if (!notification || typeof notification !== "object") {
		return undefined;
	}
	const n = notification as unknown as Record<string, unknown>;
	const merged: Record<string, unknown> = {};

	const data = n.data;
	if (data && typeof data === "object" && !Array.isArray(data)) {
		Object.assign(merged, data as Record<string, unknown>);
	}

	for (const [key, value] of Object.entries(n)) {
		if (NOT_CUSTOM_PAYLOAD_KEYS.has(key) || value === undefined || value === null) {
			continue;
		}
		if (!(key in merged)) {
			merged[key] = value;
		}
	}

	return Object.keys(merged).length > 0 ? merged : undefined;
}

/** True when the notification payload requests the app-update store flow. */
export function isUpdatePushNotificationData(data: unknown): boolean {
	return getPayloadAction(data) === PUSH_DATA_ACTION_APP_UPDATE;
}

/**
 * Handles `pushNotificationActionPerformed` — merges payload then opens store if `action` is update.
 */
export function openAppStoreIfUpdateFromPushTap(performed: ActionPerformed): void {
	const merged =
		mergePushNotificationCustomPayload(performed.notification) ??
		(performed.notification as unknown as Record<string, unknown> | undefined);
	openAppStoreIfUpdatePushAction(merged);
}

/**
 * Opens this app’s listing in the Play Store (Android) or App Store (iOS).
 * Pass merged custom payload from {@link mergePushNotificationCustomPayload} when possible.
 */
export function openAppStoreIfUpdatePushAction(data: unknown): void {
	if (!Capacitor.isNativePlatform()) {
		return;
	}
	if (!isUpdatePushNotificationData(data)) {
		return;
	}
	void AppUpdate.openAppStore().catch(() => {});
}
